import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, CheckCircle2, AlertCircle, Loader2, HardDrive } from 'lucide-react';
import { Button } from './ui/button';
import * as localStore from '../services/localPlantStore';

import { API_BASE_URL } from '../utils/apiBase';
const BACKEND_URL = API_BASE_URL;

export const MigrationDialog = ({ localPlantCount, onComplete, onDismiss }) => {
  const [migrating, setMigrating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, failed: 0 });
  const [done, setDone] = useState(false);

  const handleMigrate = async () => {
    setMigrating(true);
    const plants = await localStore.getAllPlants();
    const total = plants.length;
    setProgress({ current: 0, total, failed: 0 });
    let failed = 0;
    const sessionToken = localStorage.getItem('auth_hint');

    for (let i = 0; i < plants.length; i++) {
      const plant = plants[i];
      try {
        const photo = await localStore.getPhoto(plant.plant_id);
        const payload = {
          common_name: plant.common_name || 'Unnamed Plant',
          scientific_name: plant.scientific_name || '',
          image_base64: photo || null,
          care_details: plant.care_details || {},
          incomplete: plant.incomplete || false,
        };
        await axios.post(`${BACKEND_URL}/api/plants`, payload, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
      } catch (err) {
        console.error(`Failed to migrate plant ${plant.common_name}:`, err);
        failed++;
      }
      setProgress({ current: i + 1, total, failed });
    }

    // Clear local data after migration
    try {
      await localStore.clearAllData();
      localStorage.removeItem('local_mode');
    } catch (err) {
      console.error('Failed to clear local data:', err);
    }

    setDone(true);
    setMigrating(false);

    if (failed === 0) {
      toast.success(`All ${total} plants migrated to Google Drive!`);
    } else {
      toast.warning(`${total - failed} of ${total} plants migrated. ${failed} failed — they've been cleared from local storage.`);
    }
  };

  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="migration-dialog-overlay">
      <div className="w-full max-w-md rounded-2xl p-6 shadow-xl" style={{ backgroundColor: 'white' }} data-testid="migration-dialog">

        {!migrating && !done && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(143, 28%, 92%)' }}>
                <HardDrive className="w-5 h-5" style={{ color: 'hsl(143, 28%, 30%)' }} />
              </div>
              <h2 className="text-lg font-semibold" style={{ color: 'hsl(143, 28%, 25%)' }}>
                Local plants found
              </h2>
            </div>
            <p className="text-sm mb-4" style={{ color: 'hsl(125, 12%, 25%)' }}>
              You have <strong>{localPlantCount} plant{localPlantCount !== 1 ? 's' : ''}</strong> saved on this device from local mode. Would you like to migrate them to your Google Drive?
            </p>
            <p className="text-xs mb-5" style={{ color: 'hsl(125, 12%, 42%)' }}>
              This will upload each plant and its photo to your Drive, then clear the local copy. Your plants will be available on all your devices.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={onDismiss}
                data-testid="migration-dismiss-btn"
              >
                Not now
              </Button>
              <Button
                className="flex-1"
                onClick={handleMigrate}
                style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
                data-testid="migration-start-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Migrate to Drive
              </Button>
            </div>
          </>
        )}

        {migrating && (
          <div className="text-center py-2">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: 'hsl(143, 28%, 35%)' }} />
            <h2 className="text-lg font-semibold mb-2" style={{ color: 'hsl(143, 28%, 25%)' }}>
              Migrating plants...
            </h2>
            <p className="text-sm mb-4" style={{ color: 'hsl(125, 12%, 32%)' }}>
              Uploading {progress.current} of {progress.total}
              {progress.failed > 0 && <span className="text-red-500"> ({progress.failed} failed)</span>}
            </p>
            <div className="w-full rounded-full h-2 overflow-hidden" style={{ backgroundColor: 'hsl(143, 28%, 90%)' }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${pct}%`, backgroundColor: 'hsl(143, 40%, 42%)' }}
              />
            </div>
            <p className="text-xs mt-2" style={{ color: 'hsl(125, 12%, 42%)' }}>{pct}%</p>
          </div>
        )}

        {done && (
          <div className="text-center py-2">
            {progress.failed === 0 ? (
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: 'hsl(143, 40%, 42%)' }} />
            ) : (
              <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: 'hsl(40, 90%, 50%)' }} />
            )}
            <h2 className="text-lg font-semibold mb-2" style={{ color: 'hsl(143, 28%, 25%)' }}>
              {progress.failed === 0 ? 'Migration complete!' : 'Migration finished with issues'}
            </h2>
            <p className="text-sm mb-5" style={{ color: 'hsl(125, 12%, 25%)' }}>
              {progress.failed === 0
                ? `All ${progress.total} plants are now in your Google Drive.`
                : `${progress.total - progress.failed} of ${progress.total} plants uploaded. ${progress.failed} could not be migrated.`}
            </p>
            <Button
              className="w-full"
              onClick={onComplete}
              style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
              data-testid="migration-done-btn"
            >
              Continue to library
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
